'use client';

import { useEffect, useState } from 'react';

import type { PostSearchItem } from '@ps/api/types';
import { proxyFetch } from '@ps/functions/utils/proxyFetch';

import { useOnCheckoutSuccess } from '../../checkout/notification/useOnCheckoutSuccess';

interface State {
  post: PostSearchItem | undefined;
  loading: boolean;
  error: Error | undefined;
}

const kInitialState: State = {
  post: undefined,
  loading: true,
  error: undefined,
};

/**
 * Retry budget for the post-unlock refresh — the entitlement search index lags a
 * purchase, so a just-unlocked post reads `restricted` for a beat. Poll until it
 * comes back un-restricted or the budget is spent (~9s, enough for a tip's slower
 * settle). Mirrors upstream `reloadPost`'s `checkForUnlocked` loop.
 */
const kMaxUnlockAttempts = 6;
const kUnlockAttemptDelayMs = 1500;

/**
 * Process-lifetime cache of resolved posts, keyed by `creatorId:postId` — the web
 * equivalent of PopshopRN's shared `posts$` subject. Chat lists recycle rows
 * (FlashList), so the same card mounts repeatedly while scrolling; without this
 * every remount would re-fetch and flash the un-gated snapshot before the post
 * lands, drifting the list. A cache hit renders the resolved post synchronously
 * on mount, so there's no per-scroll flip.
 *
 * It's a REACTIVE store: {@link setCached} notifies every mounted hook on the
 * key, so an out-of-band refresh (a post unlock — see {@link refreshPostById})
 * lifts the gate on every card of that post at once, not just the one that
 * triggered it.
 */
const postCache = new Map<string, PostSearchItem>();
const cacheSubscribers = new Map<string, Set<() => void>>();
/** In-flight refreshes, keyed like the cache — dedupes concurrent card triggers. */
const refreshInFlight = new Map<string, Promise<void>>();

function cacheKey(creatorId: string, postId: string): string {
  return `${creatorId}:${postId}`;
}

/**
 * Publish a post the caller ALREADY holds, so a consumer mounting next renders
 * it synchronously instead of re-fetching it. The manage-posts row hands over
 * the row it drew when the creator taps Edit.
 *
 * A cached entry that is NEWER is kept: the list's copy can predate a refresh
 * this session already did (e.g. right after a save), and seeding blindly would
 * put the older one back.
 */
export function primePostCache(creatorId: string, post: PostSearchItem): void {
  if (!creatorId || !post.id) return;
  const key = cacheKey(creatorId, post.id);
  const existing = postCache.get(key);
  // ISO-8601, so lexicographic order is chronological order.
  if (existing != null && existing.updatedAt > post.updatedAt) return;
  setCached(key, post);
}

/**
 * Drop a post from the cache — for after a WRITE, where every copy in memory is
 * now wrong. Subscribers are deliberately not notified: a mounted consumer
 * keeping what it has is better than blanking it, and the next mount refetches.
 */
export function invalidatePostCache(creatorId: string, postId: string): void {
  if (!creatorId || !postId) return;
  postCache.delete(cacheKey(creatorId, postId));
}

function subscribeCache(key: string, listener: () => void): () => void {
  let set = cacheSubscribers.get(key);
  if (!set) {
    set = new Set();
    cacheSubscribers.set(key, set);
  }
  set.add(listener);
  return () => {
    set.delete(listener);
  };
}

function setCached(key: string, post: PostSearchItem): void {
  postCache.set(key, post);
  const set = cacheSubscribers.get(key);
  if (!set) return;
  for (const listener of Array.from(set)) {
    listener();
  }
}

async function fetchPostSearchItem(
  storeId: string,
  postId: string,
): Promise<PostSearchItem | undefined> {
  const params = new URLSearchParams({ storeId, postId });
  const post = await proxyFetch<PostSearchItem | null>(
    `/api/bio/posts/search-item?${params.toString()}`,
  );
  return post ?? undefined;
}

function delay(ms: number): Promise<void> {
  return new Promise(resolve => setTimeout(resolve, ms));
}

/**
 * Re-fetch a post out-of-band and publish it to the reactive cache — used after
 * an unlock so every mounted card lifts its gate. When `checkForUnlocked`, a
 * still-`restricted` result is treated as "the index hasn't caught up" and the
 * fetch retries (bounded by {@link kMaxUnlockAttempts}) rather than publishing a
 * result that would keep the paywall up. Deduped per key so N cards of the same
 * post reacting to one event share ONE poll.
 */
export function refreshPostById(
  storeId: string,
  postId: string,
  { checkForUnlocked }: { checkForUnlocked: boolean },
): Promise<void> {
  const key = cacheKey(storeId, postId);
  const existing = refreshInFlight.get(key);
  if (existing) return existing;

  const run = (async () => {
    try {
      for (let attempt = 1; attempt <= kMaxUnlockAttempts; attempt++) {
        const post = await fetchPostSearchItem(storeId, postId);
        if (post) {
          // Always publish the latest — a crowd-tip's progress updates even while
          // the post stays restricted (goal not met yet).
          setCached(key, post);
          // Keep polling for un-restriction (the index lags the order); stop once
          // it un-restricts or the budget is spent.
          if (!checkForUnlocked || !post.restricted) return;
        }
        if (attempt < kMaxUnlockAttempts) await delay(kUnlockAttemptDelayMs);
      }
    } catch (error) {
      console.error('Post refresh after unlock failed:', error);
    } finally {
      refreshInFlight.delete(key);
    }
  })();

  refreshInFlight.set(key, run);
  return run;
}

export interface UseFetchPostByIdOptions {
  /**
   * Refresh a cache HIT in the background. For a writer surface (the post
   * editor), where a seeded or long-cached copy may be stale; a viewer surface
   * wants the default, since re-fetching per remount is what the cache exists
   * to prevent.
   */
  revalidate?: boolean;
}

/**
 * Fetch a single post by `(storeId, postId)` via the search API. The
 * PopshopRN counterpart added a Firestore-direct branch for the
 * post's own author so creators see uncached writes; that's deferred
 * here — the search API works for both branches with a small staleness
 * window.
 *
 * Returns the raw `PostSearchItem` rather than the full `Post` model
 * — chat-bubble consumers only need title / description / medias /
 * counts, all of which the search response carries directly.
 *
 * Reacts to checkout completions: when a pay/tip purchase of THIS post — or any
 * community subscription (a tier change may unlock it) — lands, it refreshes
 * (retrying past the index lag), so a card behind the checkout overlay lifts its
 * paywall without a reload.
 */
export function useFetchPostById(
  storeId: string | undefined,
  postId: string | undefined,
  { revalidate = false }: UseFetchPostByIdOptions = {},
): State {
  const [state, setState] = useState<State>(() => {
    if (storeId && postId) {
      const cached = postCache.get(cacheKey(storeId, postId));
      if (cached) return { post: cached, loading: false, error: undefined };
    }
    return kInitialState;
  });

  useEffect(() => {
    if (!storeId || !postId) {
      setState({ post: undefined, loading: false, error: undefined });
      return;
    }
    let cancelled = false;
    const key = cacheKey(storeId, postId);

    // Re-render whenever the shared cache for this key changes (an unlock refresh
    // from any card, or a sibling's initial fetch landing).
    const unsubscribe = subscribeCache(key, () => {
      const cached = postCache.get(key);
      if (!cancelled && cached) {
        setState({ post: cached, loading: false, error: undefined });
      }
    });

    const cached = postCache.get(key);
    // Only surface a loading state on a cache miss — a hit already rendered the
    // resolved post synchronously, so re-showing "loading" would flip it.
    if (cached) {
      setState({ post: cached, loading: false, error: undefined });
      if (revalidate) {
        // Publish a newer post; never blank a rendered one. A deleted post
        // (null) or a failed request must not wipe a form someone is editing —
        // and `setCached` re-renders this hook through its own subscription.
        fetchPostSearchItem(storeId, postId)
          .then(post => {
            if (post) setCached(key, post);
          })
          .catch((error: unknown) => {
            console.error('Post revalidation failed:', error);
          });
      }
    } else {
      setState({ post: undefined, loading: true, error: undefined });
      fetchPostSearchItem(storeId, postId)
        .then(post => {
          if (post) setCached(key, post);
          if (cancelled) return;
          setState({ post, loading: false, error: undefined });
        })
        .catch((error: unknown) => {
          if (cancelled) return;
          setState({
            post: undefined,
            loading: false,
            error: error instanceof Error ? error : new Error(String(error)),
          });
        });
    }

    return () => {
      cancelled = true;
      unsubscribe();
    };
  }, [postId, storeId, revalidate]);

  useOnCheckoutSuccess(event => {
    if (!storeId || !postId) return;
    const unlocksThisPost =
      (event.type === 'post' && event.params.post_id === postId) ||
      (event.type === 'tip' && event.params.tip_ref === postId);
    // A community subscription unlocks by tier, not a specific post — refresh so
    // this card re-checks whether the new tier lifts its gate.
    const tierChanged = event.type === 'community_membership_subscription';
    if (unlocksThisPost || tierChanged) {
      void refreshPostById(storeId, postId, { checkForUnlocked: true });
    }
  });

  return state;
}
